import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Check } from 'lucide-react';

const About = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const features = [
    'Premium Materials',
    'Curated Collections',
    'Design Consultation',
    'Trusted Quality Brands',
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      data-testid="about-section"
      className="py-24 md:py-32 lg:py-40 bg-stone-white"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            className="relative overflow-hidden order-2 lg:order-1"
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="aspect-[4/5] relative">
              <img
                src="https://images.pexels.com/photos/2102546/pexels-photo-2102546.jpeg?_gl=1*qisag0*_ga*MTEwNzAyNjQ3LjE3NzE0MDQ5OTY.*_ga_8JE65Q40S6*czE3NzE0OTEwMjckbzMkZzEkdDE3NzE0OTExNTkkajYwJGwwJGgw"
                alt="Premium tile interior"
                data-testid="about-image"
                className="w-full h-full object-cover"
              />
              {/* Decorative Frame */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-burgundy/30 -z-10" />
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          >
            <span
              data-testid="about-label"
              className="text-xs uppercase tracking-[0.2em] text-burgundy font-medium"
            >
              About Us
            </span>
            <h2
              data-testid="about-heading"
              className="font-playfair text-3xl md:text-4xl lg:text-5xl text-matte-charcoal mt-4 mb-6 leading-tight"
            >
              Crafted for Spaces
              <br />
              That Inspire
            </h2>
            <p
              data-testid="about-description"
              className="text-base md:text-lg text-stone-grey leading-relaxed mb-8"
            >
              Tilerra is a premium tile studio curating the finest surfaces from
              leading quality brands. We bring together materials that define
              character and design that transforms spaces — from contemporary
              homes to statement commercial interiors.
            </p>

            {/* Features */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                >
                  <div className="w-5 h-5 bg-burgundy/10 flex items-center justify-center">
                    <Check size={12} className="text-burgundy" />
                  </div>
                  <span
                    data-testid={`about-feature-${index}`}
                    className="text-sm text-matte-charcoal font-medium"
                  >
                    {feature}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
